import _ from "lodash";
/** external */
import React, { useEffect, useState, useCallback } from "react";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import { Checkbox, TextAreaInput, Tooltip } from "@toddle-edu/ds-web";
import classNames from "classnames";

/** relative */
import classes from "./GenericCheckBox.scss";

const styles = {
  tooltipOverlayStyle: {
    maxWidth: "320px",
  },
};

const DEBOUNCE_DELAY = 300; // 300ms delay for debouncing

const GenericCheckBox = props => {
  const {
    item,
    showSubtextUnderInfo,
    tooltipPlacement = "bottom",
    fixArrowPosition = false,
    onChange,
    showTextBoxOnSelect,
    isSelected,
    textboxValue,
    isRootItem,
    disabled,
    testId,
  } = props;

  // Add state to manage input value locally
  const [inputValue, setInputValue] = useState(textboxValue || "");

  // Update local state when prop changes
  useEffect(() => {
    setInputValue(textboxValue || "");
  }, [textboxValue]);

  const { id, label, subText, textboxUid, textboxPlaceholder } = item;

  const showTextBox = showTextBoxOnSelect && isSelected;

  const infoText = showSubtextUnderInfo ? subText : null;
  const showSubText = showSubtextUnderInfo ? null : subText;

  // Create a debounced function for text changes
  const debouncedTextChange = useCallback(
    _.debounce((checkboxId, updatedText, textboxUid) => {
      onChange({
        checkboxId,
        actionType: "UPDATE_TEXT",
        updatedText,
        textboxUid,
      });
    }, DEBOUNCE_DELAY),
    [onChange]
  );

  // Clean up the debounce timer when component unmounts
  useEffect(() => {
    return () => {
      debouncedTextChange.cancel();
    };
  }, [debouncedTextChange]);

  const onInfoIconClick = e => {
    e.stopPropagation();
  };

  const onCheckBoxClick = e => {
    if (disabled) return;

    e.stopPropagation();
    onChange({
      checkboxId: id,
      actionType: isSelected ? "DESELECT" : "SELECT",
      textboxUid,
    });
  };

  const onTextboxChange = e => {
    e.stopPropagation();
    // Update the local state immediately for a responsive UI
    const newValue = e.target.value;
    setInputValue(newValue);

    // Debounce the onChange call to the parent component
    debouncedTextChange(id, newValue, textboxUid);
  };

  const onTextBoxClick = e => e.stopPropagation();

  return (
    <div
      className={classNames(classes.genericCheckBoxContainer, {
        [classes.genericCheckBoxWithTextBoxPadding]: showTextBox,
      })}
      onClick={onCheckBoxClick}
    >
      <div>
        <Checkbox
          dsVersion="2.0"
          isChecked={isSelected}
          onClick={onCheckBoxClick}
          disabled={disabled}
          testId={testId}
        />
      </div>
      <div className={classes.contentContainer}>
        <div className={classes.labelAndSubTextContainer}>
          <span
            className={classNames(classes.label, {
              ["text-body"]: !showSubText,
              ["text-label"]: showSubText || isRootItem,
            })}
          >
            {label}
            {infoText ? (
              <span className={classes.infoIcon}>
                <Tooltip
                  tooltip={infoText}
                  dsVersion="2.0"
                  placement={tooltipPlacement}
                  showArrow={true}
                  fixArrowPosition={fixArrowPosition}
                  overlayInnerStyle={styles.tooltipOverlayStyle}
                >
                  <InformationOutlined
                    variant="subtle"
                    size="xxx-small"
                    onClick={onInfoIconClick}
                  />
                </Tooltip>
              </span>
            ) : null}
          </span>

          {showSubText ? (
            <span className={classNames("text-body", classes.subText)}>
              {subText}
            </span>
          ) : null}
        </div>
        {showTextBox ? (
          <div>
            <TextAreaInput
              dsVersion="2.0"
              value={inputValue}
              onChange={onTextboxChange}
              onClick={onTextBoxClick}
              placeholder={textboxPlaceholder}
              minRows={2}
              className={classes.textboxPlaceholder}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GenericCheckBox;
